import React, { createContext, useContext, useEffect, useState } from 'react';
import { Amplify } from 'aws-amplify';
import { 
  getCurrentUser, 
  signIn, 
  signOut, 
  fetchAuthSession,
  fetchUserAttributes 
} from 'aws-amplify/auth';
import { cognitoConfig } from '../components/Constants';

// Amplify'i yapılandır (v6 API)
if (cognitoConfig.userPoolId && cognitoConfig.userPoolWebClientId) {
  const amplifyConfig = {
    Auth: {
      Cognito: {
        userPoolId: cognitoConfig.userPoolId,
        userPoolClientId: cognitoConfig.userPoolWebClientId,
        region: cognitoConfig.region,
      }
    }
  };

  // Identity Pool ID varsa ekle (Admin API erişimi için)
  if (cognitoConfig.identityPoolId) {
    amplifyConfig.Auth.Cognito.identityPoolId = cognitoConfig.identityPoolId;
  }

  Amplify.configure(amplifyConfig);
}

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [userRole, setUserRole] = useState(null);
  const [userRegions, setUserRegions] = useState([]); // Kullanıcının atanan bölgeleri
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  // Kullanıcı rollerini belirle
  const getUserRole = async (attributes) => {
    console.log('🔍 Kullanıcı attributes:', attributes);
    
    // Önce custom attribute'dan kontrol et
    const customRole = attributes['custom:role'];
    if (customRole) {
      console.log('✅ Role bulundu (custom attribute):', customRole);
      return customRole;
    }
    
    // Sonra groups'dan kontrol et
    try {
      const session = await fetchAuthSession();
      console.log('🔍 Auth session:', session);
      
      // Access token'dan groups'u al
      const accessToken = session.tokens?.accessToken;
      if (accessToken) {
        const payload = accessToken.payload;
        console.log('🔍 Token payload:', payload);
        
        // Cognito groups genellikle 'cognito:groups' claim'inde olur
        const groups = payload['cognito:groups'] || payload['groups'] || [];
        console.log('🔍 Groups:', groups);
        
        if (groups.length > 0) {
          const role = groups[0];
          console.log('✅ Role bulundu (groups):', role);
          return role;
        }
      }
      
      // ID token'dan da kontrol et
      const idToken = session.tokens?.idToken;
      if (idToken) {
        const payload = idToken.payload;
        const groups = payload['cognito:groups'] || payload['groups'] || [];
        if (groups.length > 0) {
          console.log('✅ Role bulundu (ID token groups):', groups[0]);
          return groups[0];
        }
      }
    } catch (error) {
      console.error('❌ Error fetching user groups:', error);
    }
    
    console.log('⚠️ Role bulunamadı');
    return null;
  };

  // Kullanıcı bilgilerini yükle
  const loadUser = async () => {
    try {
      setLoading(true);
      const currentUser = await getCurrentUser();
      const attributes = await fetchUserAttributes();
      const role = await getUserRole(attributes);
      
      // Bölge bilgisini al (custom:regions attribute'undan)
      let regions = [];
      const regionsAttr = attributes['custom:regions'];
      if (regionsAttr) {
        try {
          regions = JSON.parse(regionsAttr);
        } catch (e) {
          // Eğer JSON değilse, virgülle ayrılmış string olabilir
          regions = regionsAttr.split(',').map(r => r.trim()).filter(Boolean);
        }
      }
      
      setUser({
        username: currentUser.username,
        email: attributes.email || attributes['email'],
        attributes: attributes,
      });
      setUserRole(role);
      setUserRegions(regions);
      setIsAuthenticated(true);
    } catch (error) {
      console.log('No authenticated user:', error);
      setUser(null);
      setUserRole(null);
      setUserRegions([]);
      setIsAuthenticated(false);
    } finally {
      setLoading(false);
    }
  };

  // Hata mesajlarını Türkçe'ye çevir
  const translateError = (error) => {
    const errorMessage = error.message || error.toString() || '';
    const errorCode = error.name || error.__type || '';
    
    // AWS Cognito hata kodları ve mesajları
    if (errorMessage.includes('User pool client') && errorMessage.includes('does not exist') || errorCode === 'ResourceNotFoundException') {
      return 'Cognito yapılandırması hatalı. Lütfen .env.local dosyasındaki Client ID değerini kontrol edin.';
    }
    
    if (errorMessage.includes('User does not exist') || errorCode === 'UserNotFoundException') {
      return 'Bu kullanıcı bulunamadı.';
    }
    
    if (errorMessage.includes('Incorrect username or password') || errorCode === 'NotAuthorizedException') {
      if (errorMessage.includes('SECRET_HASH')) {
        return 'Cognito yapılandırması hatalı. Lütfen App Client\'ı "Don\'t generate a client secret" seçeneği ile yeniden oluşturun.';
      }
      return 'Kullanıcı adı veya şifre hatalı.';
    }
    
    if (errorMessage.includes('User is not confirmed') || errorCode === 'UserNotConfirmedException') {
      return 'Hesabınız henüz onaylanmamış. Lütfen e-posta adresinizi kontrol edin.';
    }
    
    if (errorMessage.includes('Password attempts exceeded') || errorCode === 'TooManyFailedAttemptsException') {
      return 'Çok fazla başarısız giriş denemesi. Lütfen daha sonra tekrar deneyin.';
    }
    
    if (errorMessage.includes('User is disabled') || errorCode === 'UserLambdaValidationException') {
      return 'Hesabınız devre dışı bırakılmış.';
    }
    
    if (errorMessage.includes('Network error') || errorMessage.includes('Failed to fetch')) {
      return 'Bağlantı hatası. Lütfen internet bağlantınızı kontrol edin.';
    }
    
    // Genel hata mesajı
    return 'Giriş yapılırken bir hata oluştu. Lütfen tekrar deneyin.';
  };

  // Login fonksiyonu
  const login = async (username, password) => {
    try {
      const { isSignedIn, nextStep } = await signIn({ username, password });
      
      // Eğer şifre değiştirme gerekiyorsa
      if (nextStep?.signInStep === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED') {
        return {
          success: false,
          error: 'PASSWORD_CHANGE_REQUIRED',
          challengeName: 'NEW_PASSWORD_REQUIRED',
          session: nextStep.additionalInfo?.session
        };
      }
      
      if (isSignedIn) {
        await loadUser();
        
        // Login logunu kaydet
        try {
          const { logEvent } = await import('../services/logService');
          const userData = await getCurrentUser();
          const attributes = await fetchUserAttributes();
          const role = await getUserRole(attributes);
          await logEvent('INFO', 'login', `Kullanıcı girişi: ${attributes.email || username}`, {
            userEmail: attributes.email || username,
            userRole: role,
          });
        } catch (logError) {
          // Log hatası kritik değil
          console.warn('Log kaydedilemedi:', logError);
        }
        
        return { success: true };
      }
      return { success: false, error: 'Giriş başarısız' };
    } catch (error) {
      console.error('Login error:', error);
      
      // Password change required hatası
      if (error.name === 'NewPasswordRequiredError' || error.code === 'NewPasswordRequired') {
        return {
          success: false,
          error: 'PASSWORD_CHANGE_REQUIRED',
          challengeName: 'NEW_PASSWORD_REQUIRED',
          session: error.session
        };
      }
      
      const translatedError = translateError(error);
      return { 
        success: false, 
        error: translatedError
      };
    }
  };

  // Şifre değiştirme fonksiyonu
  const changePassword = async (newPassword, session) => {
    try {
      const { confirmSignIn } = await import('aws-amplify/auth');
      await confirmSignIn({
        challengeResponse: newPassword,
        options: {
          userAttributes: {}
        }
      });
      await loadUser();
      return { success: true };
    } catch (error) {
      console.error('Password change error:', error);
      const translatedError = translateError(error);
      return {
        success: false,
        error: translatedError
      };
    }
  };

  // Logout fonksiyonu
  const logout = async () => {
    try {
      // Logout logunu kaydet (çıkış yapmadan önce)
      try {
        const { logEvent } = await import('../services/logService');
        await logEvent('INFO', 'logout', `Kullanıcı çıkışı: ${user?.email || 'Bilinmeyen'}`, {
          userEmail: user?.email,
          userRole: userRole,
        });
      } catch (logError) {
        // Log hatası kritik değil
        console.warn('Log kaydedilemedi:', logError);
      }
      
      await signOut();
      setUser(null);
      setUserRole(null);
      setUserRegions([]);
      setIsAuthenticated(false);
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  // Role kontrolü
  const hasRole = (role) => {
    if (!userRole) return false;
    return userRole === role || userRole.toLowerCase() === role.toLowerCase();
  };

  // Role kontrolü (array)
  const hasAnyRole = (roles) => {
    if (!userRole) return false;
    return roles.some(role => 
      userRole === role || userRole.toLowerCase() === role.toLowerCase()
    );
  };

  // İlk yüklemede kullanıcıyı kontrol et
  useEffect(() => {
    loadUser();
  }, []);

  const value = {
    user,
    userRole,
    userRegions,
    isAuthenticated,
    loading,
    login,
    logout,
    changePassword,
    hasRole,
    hasAnyRole,
    refreshUser: loadUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

